<script setup lang="ts">
import { computed, onMounted } from 'vue';
import CheckIcon from '@/components/icons/CheckIcon.vue';
import PlsButton1 from '@/components/PlsButton1.vue';
import { useI18n } from '@/plugins/i18n';
import { analytics } from '@/common/logs';
import { PAGE_EVENTS, BTN_EVTS } from '@/common/logs/events';
import { useTicketConfirmationAllCustomisation } from '../../composables/guest-form/useTicketConfirmationCustomisation';
import { useGuestFormPrimaryActionButton } from '../../composables/guest-form/useGuestFormActionsCustomisation';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';

interface Props {
  ticketId: string;
  email: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();

const ticketCustomisation = useTicketConfirmationAllCustomisation();
const { successIcon, title, idText, idValue, createdText, nextStepsTitle, nextStepsStepIcon, nextSteps } =
  ticketCustomisation;

const primaryAction = useGuestFormPrimaryActionButton();

const interpolate = (template: string, vars: Record<string, string>): string => {
  let result = template;
  for (const [key, value] of Object.entries(vars)) {
    result = result.split(`{${key}}`).join(value);
  }
  return result;
};

const titleDisplay = computed(() => {
  const custom = title.titleText.value?.trim();
  return custom || (t('page.confirmation.ticket_created.title') as string);
});

const titleStyle = computed(() => createTextStyle(title.titleTextColor));

const ticketIdLabel = computed(() => {
  const custom = idText.text.value?.trim();
  return custom || (t('page.confirmation.ticket_created.ticket_id') as string);
});

const ticketIdLabelStyle = computed(() => createTextStyle(idText.textColor));

const ticketIdValueStyle = computed(() => createTextStyle(idValue.textColor));

const successIconStyle = computed(() => createTextStyle(successIcon.iconColor));

const nextStepIconStyle = computed(() => createTextStyle(nextStepsStepIcon.iconColor));

const messageText = computed(() => {
  const custom = createdText.text.value?.trim();
  if (custom) {
    return interpolate(custom, { email: props.email });
  }
  return t('page.confirmation.ticket_created.message', { email: props.email }) as string;
});

const messageStyle = computed(() => createTextStyle(createdText.textColor));

const nextStepsHeading = computed(() => {
  const custom = nextStepsTitle.titleText.value?.trim();
  return custom || (t('page.confirmation.ticket_created.next_steps') as string);
});

const nextStepsHeadingStyle = computed(() => createTextStyle(nextStepsTitle.titleTextColor));

const step1 = computed(() => {
  const custom = nextSteps.step1Text.value?.trim();
  return custom || (t('page.confirmation.ticket_created.step_1') as string);
});

const step2 = computed(() => {
  const custom = nextSteps.step2Text.value?.trim();
  return custom || (t('page.confirmation.ticket_created.step_2') as string);
});

const step3 = computed(() => {
  const custom = nextSteps.step3Text.value?.trim();
  return custom || (t('page.confirmation.ticket_created.step_3') as string);
});

const step1Style = computed(() => createTextStyle(nextSteps.step1TextColor));
const step2Style = computed(() => createTextStyle(nextSteps.step2TextColor));
const step3Style = computed(() => createTextStyle(nextSteps.step3TextColor));

const closeButtonText = computed(() => {
  // TODO: use customisation for close button of ticket confirmation
  // const custom = primaryAction.buttonText.value?.trim();
  return t('page.confirmation.ticket_created.close') as string;
});

const closeButtonBackgroundColor = computed(() => primaryAction.buttonBackgroundColor.value ?? undefined);
const closeButtonBorderColor = computed(() => primaryAction.buttonBorderColor.value ?? undefined);
const closeButtonTextColor = computed(() => primaryAction.buttonTextColor.value ?? undefined);

const handleClose = () => {
  analytics.trackClick({
    name: BTN_EVTS.GUEST_TICKET_CONFIRMATION_CLOSE,
    metadata: {
      ticketId: props.ticketId,
      email: props.email,
    },
  });
  emit('close');
};

onMounted(() => {
  analytics.trackPageEvent({
    name: PAGE_EVENTS.GUEST_TICKET_CONFIRMATION,
    enter: true,
    metadata: {
      ticketId: props.ticketId,
      email: props.email,
    },
  });
});
</script>

<template>
  <div
    class="shadow-hard theme-font-main widget-guest-ticket"
    role="region"
    data-testid="ticket-confirmation"
    :aria-label="titleDisplay"
  >
    <div class="widget-guest-ticket__inner">
      <div class="widget-guest-ticket__icon" :style="successIconStyle" aria-hidden="true">
        <CheckIcon />
      </div>

      <div class="theme-font-h4 widget-guest-ticket__title" :style="titleStyle">
        {{ titleDisplay }}
      </div>

      <div class="widget-guest-ticket__ticket-info">
        <div class="widget-guest-ticket__ticket-id">
          <span class="theme-font-main widget-guest-ticket__ticket-label" :style="ticketIdLabelStyle">
            {{ ticketIdLabel }}
          </span>
          <span
            class="theme-font-h4 widget-guest-ticket__ticket-value"
            data-testid="ticket-confirmation-id"
            :style="ticketIdValueStyle"
            >{{ ticketId }}</span
          >
        </div>
      </div>

      <p class="theme-font-main widget-guest-ticket__message" :style="messageStyle">
        {{ messageText }}
      </p>

      <div class="widget-guest-ticket__next-steps">
        <h4 class="theme-font-main widget-guest-ticket__next-title" :style="nextStepsHeadingStyle">
          {{ nextStepsHeading }}
        </h4>
        <ul class="widget-guest-ticket__step-list">
          <li class="theme-font-main widget-guest-ticket__step" :style="step1Style">
            <CheckIcon class="widget-guest-ticket__step-icon" :style="nextStepIconStyle" aria-hidden="true" />
            {{ step1 }}
          </li>
          <li class="theme-font-main widget-guest-ticket__step" :style="step2Style">
            <CheckIcon class="widget-guest-ticket__step-icon" :style="nextStepIconStyle" aria-hidden="true" />
            {{ step2 }}
          </li>
          <li class="theme-font-main widget-guest-ticket__step" :style="step3Style">
            <CheckIcon class="widget-guest-ticket__step-icon" :style="nextStepIconStyle" aria-hidden="true" />
            {{ step3 }}
          </li>
        </ul>
      </div>

      <div class="widget-guest-ticket__actions">
        <PlsButton1
          :aria-label="closeButtonText"
          :border-color="closeButtonBorderColor"
          :background-color="closeButtonBackgroundColor"
          :text-color="closeButtonTextColor"
          class="theme-font-main widget-guest-ticket__close"
          @click="handleClose"
        >
          {{ closeButtonText }}
        </PlsButton1>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.widget-guest-ticket {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 24px;
}

.widget-guest-ticket__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.widget-guest-ticket__icon {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-guest-ticket__icon svg {
  width: 100%;
  height: 100%;
}

.widget-guest-ticket__ticket-info {
  width: 100%;
  max-width: 22rem;
}

.widget-guest-ticket__ticket-id {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  border-radius: 8px;
}

.widget-guest-ticket__message {
  margin: 0;
  max-width: 25rem;
}

.widget-guest-ticket__next-steps {
  text-align: left;
  width: 100%;
  max-width: 25rem;
}

.widget-guest-ticket__next-title {
  margin: 0 0 10px;
  text-align: center;
  font-weight: 700;
}

.widget-guest-ticket__step-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.widget-guest-ticket__step {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.widget-guest-ticket__step-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.widget-guest-ticket__actions {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.widget-guest-ticket__close {
  min-width: min(40vw, 200px);
  max-width: 200px;
  padding: 12px 20px;
}
</style>
